// The layout of the partnerships graph: pure, synchronous and deterministic.
// The response comes in; out come the position of each group, the shape of
// each cluster, the guides of the rings and the ties in the shape the canvas
// takes. The same response always gives the same positions.
//
// The clusters: each cluster's groups stand in rows about its middle, the
// strongest nearest it, and a soft ellipse is drawn round them with the
// cluster's name above. The clusters are placed one by one beside those
// placed already, each where the clusters it is most tied to stand, so that
// closely tied clusters are neighbours and a cluster of few ties stands at
// the edge; the groups in no cluster stand in a grid below them, in no shape.
//
// The partners of one organization: the organization stands at the centre
// and its partners on rings round it, wider than tall as the ecosystem's,
// the strongest ties on the inner ring. Each ring holds a few more than the
// one inside it; partners tied equally stand on one ring where they can.

import {
  PartnershipCluster,
  PartnershipLink,
  PartnershipNode,
  PartnershipPair,
  Partnerships,
} from "../../../types/analytics";
import {
  BULGE_FALLS,
  Box as OutlineBox,
  RoundShape,
  bulgedRound,
  roundBaseOf,
  roundOutline,
  roundOutlineOf,
  touchesRound,
} from "./clusterOutline";

export interface Point {
  x: number;
  y: number;
}

// The width a group's name wraps within, beneath its mark
export const LABEL_WIDTH = 116;
// Room for the name on two lines and the line beneath it
export const LABEL_HEIGHT = 34;
// The smallest and the largest mark: a group is sized by the strength of
// its ties, against the strongest group drawn
export const MARK_MIN = 12;
export const MARK_MAX = 34;
// The mark of the organization in focus
export const FOCUS_MARK = 40;
// The room a group keeps clear of its neighbours: its name's width and the
// largest mark with a name beneath, each with a gap
export const SLOT = { width: LABEL_WIDTH + 14, height: MARK_MAX + LABEL_HEIGHT + 14 } as const;
// How much wider than tall a cluster's rows are laid
const BLOCK_ASPECT = 1.6;
// The share of a name's width that is held inside the outline: a name is
// seldom as wide as the room it may wrap in
const NAME_SHARE = 0.42;
// Room above a shape for the cluster's name, on two lines
export const CLUSTER_LABEL_HEIGHT = 30;
// The narrowest and the widest a cluster's name wraps within
const CLUSTER_LABEL_MIN = 170;
const CLUSTER_LABEL_MOST = 300;
// The gaps between two clusters' outlines side by side and between two rows
// of them
const GAP_X = 28;
const GAP_Y = 20;
// The room between a cluster's groups and its outline, drawn near an
// ellipse round them
const CLUSTER_MARGIN = 16;
// Room above the groups in no cluster for the words that say so
export const LOOSE_LABEL_HEIGHT = 22;
// How much wider than tall the drawing of the clusters is laid
const SPREAD_ASPECT = 1.7;
// How much wider than tall a ring is, as the ecosystem's
export const RING_ASPECT = 1.75;
// The least height of ring 1 above the centre: clear of the centre's name
const MIN_RING_ONE = 130;
// The least a ring stands above the ring inside it
const MIN_RING_STEP = 120;
// The step a ring is grown by while its partners clash
const RING_GROW = 4;
// Partners on the first ring; each ring out holds as many more
const RING_CAPACITY = 6;
// How far over its share a ring may take partners tied as strongly as the
// last it holds
const RING_STRETCH = 1.5;
const TURN = 2 * Math.PI;
const TOP = -Math.PI / 2;

export interface PlacedGroup {
  node: PartnershipNode;
  // The centre of its mark
  x: number;
  y: number;
  // The diameter of its mark
  size: number;
  // Its ring in the partners mode, 0 for the focus; null in the clusters
  ring: number | null;
  // The strength of its tie to the focus in the partners mode; null for
  // the focus and in the clusters
  tie: number | null;
  // The clusters drawn it belongs to, its own first; more than one for a
  // key player at work in several, which stands where their shapes overlap
  clusters: number[];
}

export interface PlacedClusterShape {
  cluster: PartnershipCluster;
  // The middle of its outline's extent
  cx: number;
  cy: number;
  // Its outline round its groups, an SVG path in the coordinates of the box
  // it is drawn in
  path: string;
  // The box the shape is drawn in: its name above the outline
  left: number;
  top: number;
  width: number;
  height: number;
  // The middle of its name, from the box's left, and the width it wraps
  // within
  labelX: number;
  labelWidth: number;
}

// A tie with its two ends as drawn
export interface PlacedTie {
  // `a|b`
  id: string;
  pair: PartnershipPair;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  // Both ends in one cluster
  inside: boolean;
  // One end is the organization in focus
  spoke: boolean;
}

export interface PlacedRing {
  ring: number;
  rx: number;
  ry: number;
}

export interface PartnershipsLayout {
  nodes: PlacedGroup[];
  shapes: PlacedClusterShape[];
  ties: PlacedTie[];
  rings: PlacedRing[];
  // Where the words over the groups in no cluster stand: their middle and
  // their top; null where every group is in a cluster
  loose: Point | null;
}

// Rounded to a tenth of a pixel, so that positions compare stably
const round = (value: number): number => Math.round(value * 10) / 10;

export const tieId = (pair: Pick<PartnershipPair, "a" | "b">): string => `${pair.a}|${pair.b}`;

// The diameter of a group's mark: by the square root of its strength
// against the strongest drawn, so that the area reads as the strength
export const markSize = (strength: number, most: number): number =>
  most > 0
    ? round(MARK_MIN + (MARK_MAX - MARK_MIN) * Math.sqrt(Math.max(0, strength) / most))
    : MARK_MIN;

// The clusters returned a group belongs to: its own first, then those it
// belongs to as well, the largest share first
export const membershipsOf = (data: Partnerships, node: PartnershipNode): number[] => {
  const known = new Set(data.clusters.map((cluster) => cluster.id));
  return [node.cluster, ...(node.also ?? []).map((membership) => membership.cluster)].filter(
    (cluster): cluster is number => cluster !== null && known.has(cluster),
  );
};

// The groups of each cluster returned that belong to it alone, in the
// response's order: the strongest first. A group of a cluster not returned
// is in none; a group of several stands apart, where their shapes overlap.
export const membersByCluster = (data: Partnerships): Map<number, PartnershipNode[]> => {
  const members = new Map<number, PartnershipNode[]>();
  for (const node of data.nodes) {
    const clusters = membershipsOf(data, node);
    if (clusters.length === 1) {
      const held = members.get(clusters[0]) ?? [];
      held.push(node);
      members.set(clusters[0], held);
    }
  }
  return members;
};

// The cluster a group is drawn in, or null: its own where it is returned,
// else the first it belongs to as well
export const clusterOf = (data: Partnerships, node: PartnershipNode): number | null =>
  membershipsOf(data, node)[0] ?? null;

// The places of `count` groups about a middle, in rows: as many rows as
// keep the block about half again as wide as tall, the extra groups in the
// middle rows, a row that holds as many as the row above set half a place
// over. The places come nearest the middle first, so that the strongest
// group stands at it.
export const blockPlaces = (count: number): Point[] => {
  if (count <= 0) {
    return [];
  }
  const rows = Math.min(
    count,
    Math.max(1, Math.round(Math.sqrt((count * SLOT.width) / (SLOT.height * BLOCK_ASPECT)))),
  );
  const middle = (rows - 1) / 2;
  const held = Array.from({ length: rows }, () => Math.floor(count / rows));
  Array.from({ length: rows }, (_unused, row) => row)
    .sort((a, b) => Math.abs(a - middle) - Math.abs(b - middle) || a - b)
    .slice(0, count % rows)
    .forEach((row) => {
      held[row] += 1;
    });
  const places: Point[] = [];
  let shift = 0;
  held.forEach((inRow, row) => {
    shift = row > 0 && inRow === held[row - 1] && shift === 0 ? SLOT.width / 2 : 0;
    for (let index = 0; index < inRow; index += 1) {
      places.push({
        x: (index - (inRow - 1) / 2) * SLOT.width + shift,
        y: (row - middle) * SLOT.height,
      });
    }
  });
  const xs = places.map((place) => place.x);
  const across = (Math.min(...xs) + Math.max(...xs)) / 2;
  const away = (place: Point) => (place.x / SLOT.width) ** 2 + (place.y / SLOT.height) ** 2;
  return places
    .map((place) => ({ x: place.x - across, y: place.y }))
    .sort((a, b) => away(a) - away(b) || a.y - b.y || a.x - b.x);
};

// The ties between the groups placed, the weakest first, so that the
// strongest is drawn over the rest
const placeTies = (
  data: Partnerships,
  at: Map<string, PlacedGroup>,
  focusId: string | null,
): PlacedTie[] => {
  const clustersById = new Map(data.nodes.map((node) => [node.id, membershipsOf(data, node)]));
  return data.pairs
    .filter((pair) => at.has(pair.a) && at.has(pair.b) && pair.a !== pair.b)
    .map((pair) => {
      const from = at.get(pair.a)!;
      const to = at.get(pair.b)!;
      const other = clustersById.get(pair.b) ?? [];
      return {
        id: tieId(pair),
        pair,
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
        // Both ends in one cluster, a shared group in any of its own
        inside: (clustersById.get(pair.a) ?? []).some((cluster) => other.includes(cluster)),
        spoke: focusId !== null && (pair.a === focusId || pair.b === focusId),
      };
    })
    .reverse();
};

interface Block {
  cluster: PartnershipCluster;
  members: PartnershipNode[];
  // Each member's place about the block's ellipse middle, and its mark
  places: Point[];
  sizes: number[];
  rx: number;
  ry: number;
  labelWidth: number;
  // The room the block takes: its ellipse or its name, the wider, and its
  // name above its ellipse
  width: number;
  height: number;
}

// The clusters: each a block of its groups in an ellipse, the blocks placed
// by their links, the groups in no cluster in a grid below
export const layoutClusters = (data: Partnerships): PartnershipsLayout => {
  const most = Math.max(0, ...data.nodes.map((node) => node.strength));
  const members = membersByCluster(data);
  const memberships = new Map(data.nodes.map((node) => [node.id, membershipsOf(data, node)]));
  const sharing = data.nodes.filter((node) => (memberships.get(node.id) ?? []).length > 1);
  const blocks: Block[] = data.clusters
    .filter(
      (cluster) =>
        (members.get(cluster.id) ?? []).length > 0 ||
        sharing.some((node) => memberships.get(node.id)?.includes(cluster.id)),
    )
    .map((cluster) => {
      const held = members.get(cluster.id) ?? [];
      const raw = blockPlaces(held.length);
      const sizes = held.map((node) => markSize(node.strength, most));
      // A cluster of shared groups alone holds the room of one
      // The room of its rounded outline round its own groups; a cluster of
      // shared groups alone holds the room of one
      const outline = roundOutlineOf(
        raw.map((place, index) => outlineBox({ ...place, size: sizes[index] })),
        CLUSTER_MARGIN,
      );
      const shape = outline
        ? {
            cx: (outline.bounds.left + outline.bounds.right) / 2,
            cy: (outline.bounds.top + outline.bounds.bottom) / 2,
            rx: (outline.bounds.right - outline.bounds.left) / 2,
            ry: (outline.bounds.bottom - outline.bounds.top) / 2,
          }
        : { cx: 0, cy: 0, rx: SLOT.width / 2, ry: SLOT.height / 2 };
      const labelWidth = Math.min(
        CLUSTER_LABEL_MOST,
        Math.max(CLUSTER_LABEL_MIN, shape.rx * 2),
      );
      return {
        cluster,
        members: held,
        places: raw.map((place) => ({ x: place.x - shape.cx, y: place.y - shape.cy })),
        sizes,
        rx: shape.rx,
        ry: shape.ry,
        labelWidth,
        width: Math.max(shape.rx * 2, labelWidth),
        height: CLUSTER_LABEL_HEIGHT + shape.ry * 2,
      };
    });

  const drawn = new Set([
    ...blocks.flatMap((block) => block.members.map((node) => node.id)),
    ...sharing.map((node) => node.id),
  ]);
  const loose = data.nodes.filter((node) => !drawn.has(node.id));

  // Each block where its links draw it, then its groups
  const links = data.links ?? [];
  const boxes = placeBlocks(blocks, links);
  const placed: PlacedGroup[] = [];
  for (const box of boxes) {
    const { block } = box;
    const cy = box.y - block.height / 2 + CLUSTER_LABEL_HEIGHT + block.ry;
    block.members.forEach((node, index) => {
      placed.push({
        node,
        x: round(box.x + block.places[index].x),
        y: round(cy + block.places[index].y),
        size: block.sizes[index],
        ring: null,
        tie: null,
        clusters: memberships.get(node.id) ?? [],
      });
    });
  }


  // Each cluster's shape near an ellipse round its own groups, before any
  // group it shares is set
  const bases = new Map<number, RoundShape | null>(
    boxes.map((box) => [
      box.block.cluster.id,
      roundBaseOf(
        placed.filter((entry) => entry.clusters[0] === box.block.cluster.id).map(outlineBox),
        CLUSTER_MARGIN,
      ),
    ]),
  );
  const sharedOf = new Map<number, OutlineBox[]>(boxes.map((box) => [box.block.cluster.id, []]));
  const fallsOf = new Map<number, number[]>(boxes.map((box) => [box.block.cluster.id, []]));
  const shapeOf = new Map<number, RoundShape | null>(bases);

  // A group of several clusters in the gap between their blocks, where no
  // other cluster's shape holds it and the bulges out to it hold no group
  // of another cluster
  for (const node of sharing) {
    const clusters = memberships.get(node.id) ?? [];
    const size = markSize(node.strength, most);
    const { at, reached } = sharedPlace(clusters, boxes, placed, size, {
      bases,
      sharedOf,
      fallsOf,
      shapeOf,
    });
    const entry = { node, x: round(at.x), y: round(at.y), size, ring: null, tie: null, clusters };
    placed.push(entry);
    for (const { cluster, fall } of reached) {
      const base = bases.get(cluster);
      const held = sharedOf.get(cluster);
      const falls = fallsOf.get(cluster);
      if (held && falls) {
        held.push(outlineBox(entry));
        falls.push(fall);
        shapeOf.set(cluster, base ? bulgedRound(base, held, falls) : null);
      }
    }
  }

  // Each cluster's outline; its name above the outline's top. A cluster of
  // shared groups alone is an ellipse round them.
  const shapes: PlacedClusterShape[] = [];
  for (const box of boxes) {
    const { cluster } = box.block;
    const shape = shapeOf.get(cluster.id) ?? roundBaseOf(sharedOf.get(cluster.id) ?? [], CLUSTER_MARGIN);
    if (!shape) {
      continue;
    }
    const outline = roundOutline(shape);
    const { bounds } = outline;
    const labelWidth = Math.min(
      CLUSTER_LABEL_MOST,
      Math.max(CLUSTER_LABEL_MIN, bounds.right - bounds.left),
    );
    const top = outline.top.y - CLUSTER_LABEL_HEIGHT;
    const left = Math.min(bounds.left, outline.top.x - labelWidth / 2);
    const right = Math.max(bounds.right, outline.top.x + labelWidth / 2);
    shapes.push({
      cluster,
      cx: round((bounds.left + bounds.right) / 2),
      cy: round((bounds.top + bounds.bottom) / 2),
      path: outline.path,
      left: round(left),
      top: round(top),
      width: round(right - left),
      height: round(bounds.bottom - top),
      labelX: round(outline.top.x - left),
      labelWidth: round(labelWidth),
    });
  }

  // The groups in no cluster, in a grid as wide as the clusters below
  // them, strongest first
  let label: Point | null = null;
  if (loose.length > 0) {
    const left = Math.min(0, ...boxes.map((box) => box.x - box.block.width / 2));
    const right = Math.max(0, ...boxes.map((box) => box.x + box.block.width / 2));
    const bottom = Math.max(0, ...boxes.map((box) => box.y + box.block.height / 2));
    const middle = (left + right) / 2;
    const target = Math.max(right - left, Math.ceil(Math.sqrt(loose.length * 2)) * SLOT.width);
    const columns = Math.max(1, Math.min(loose.length, Math.floor(target / SLOT.width)));
    const top = boxes.length > 0 ? bottom + GAP_Y : 0;
    const first = top + (boxes.length > 0 ? LOOSE_LABEL_HEIGHT : 0) + MARK_MAX / 2;
    label = boxes.length > 0 ? { x: round(middle), y: round(top) } : null;
    loose.forEach((node, index) => {
      const row = Math.floor(index / columns);
      const inRow = Math.min(columns, loose.length - row * columns);
      const place = index % columns;
      placed.push({
        node,
        x: round(middle + (place - (inRow - 1) / 2) * SLOT.width),
        y: round(first + row * SLOT.height),
        size: markSize(node.strength, most),
        ring: null,
        tie: null,
        clusters: [],
      });
    });
  }

  // In the response's order, whatever order they were placed in
  const order = new Map(data.nodes.map((node, index) => [node.id, index]));
  placed.sort((a, b) => (order.get(a.node.id) ?? 0) - (order.get(b.node.id) ?? 0));
  shapes.sort((a, b) => a.cluster.id - b.cluster.id);
  const at = new Map(placed.map((entry) => [entry.node.id, entry]));
  return { nodes: placed, shapes, ties: placeTies(data, at, null), rings: [], loose: label };
};

// How strongly a cluster is drawn to the middle of those placed before it,
// against the pull of its links: enough to keep the drawing compact, and to
// set a cluster of no links beside the rest rather than far off
const PULL_TO_MIDDLE = 0.35;

// How much a place that bends the drawing from the proportions aimed at
// costs, in widths of an average cluster
const SPREAD_COST = 0.6;
// How much each pixel costs that two clusters sharing groups stand further
// apart than side by side or corner to corner: a shared group can only be
// held by both where they meet
const SHARED_APART_COST = 6;
// The most passes of the second look over the placed blocks
const REFINE_PASSES = 4;

// A block placed: the middle of its box (its name above its ellipse)
interface Box {
  block: Block;
  x: number;
  y: number;
}

// The strength of the link of two clusters
type Weigh = (a: number, b: number) => number;

const linkKey = (a: number, b: number): string => `${Math.min(a, b)}|${Math.max(a, b)}`;

// A link's strength, and for each group the two clusters share the
// strength of every link drawn and more: two clusters that share a group
// stand side by side before any others
const weighing = (links: PartnershipLink[]): Weigh => {
  const all = links.reduce((sum, link) => sum + link.strength, 0) + 1;
  const weights = new Map(
    links.map((link) => [linkKey(link.a, link.b), link.strength + (link.shared ?? 0) * all]),
  );
  return (a, b) => weights.get(linkKey(a, b)) ?? 0;
};

// The gaps between two blocks side by side and one above the other: two
// that share groups stand close, with just the room of a shared group
// between them, so that their outlines overlap round it; others keep room
// for both their outlines' margins
type Gap = (a: number, b: number) => Point;

const gapping = (links: PartnershipLink[]): Gap => {
  const shared = new Map(links.map((link) => [linkKey(link.a, link.b), link.shared ?? 0]));
  return (a, b) => {
    const count = shared.get(linkKey(a, b)) ?? 0;
    return count > 0
      ? { x: SLOT.width * 0.8, y: SLOT.height * 0.8 }
      : { x: GAP_X, y: GAP_Y };
  };
};

// The order the blocks are placed in: the most linked first, then each the
// most linked to those placed already; of two as linked, the earlier in the
// response (the stronger inside)
const placingOrder = (blocks: Block[], weigh: Weigh): Block[] => {
  const linked = (block: Block, to: Block[]) =>
    to.reduce((sum, other) => sum + weigh(block.cluster.id, other.cluster.id), 0);
  const left = [...blocks];
  const order: Block[] = [];
  while (left.length > 0) {
    let pick = 0;
    let best = -1;
    left.forEach((block, index) => {
      const score = linked(block, order.length > 0 ? order : blocks);
      if (score > best + 1e-9) {
        best = score;
        pick = index;
      }
    });
    order.push(left.splice(pick, 1)[0]);
  }
  return order;
};

// The places a block may take beside a placed one: on each side and at
// each corner, its middle or its edges in line with the placed one's
const besides = (placed: Box, block: Block, gap: Point): Point[] => {
  const dx = (placed.block.width + block.width) / 2 + gap.x;
  const dy = (placed.block.height + block.height) / 2 + gap.y;
  const top = placed.y - placed.block.height / 2 + block.height / 2;
  const bottom = placed.y + placed.block.height / 2 - block.height / 2;
  const left = placed.x - placed.block.width / 2 + block.width / 2;
  const right = placed.x + placed.block.width / 2 - block.width / 2;
  return [
    { x: placed.x + dx, y: placed.y },
    { x: placed.x - dx, y: placed.y },
    { x: placed.x, y: placed.y + dy },
    { x: placed.x, y: placed.y - dy },
    { x: placed.x + dx, y: placed.y + dy },
    { x: placed.x - dx, y: placed.y + dy },
    { x: placed.x + dx, y: placed.y - dy },
    { x: placed.x - dx, y: placed.y - dy },
    { x: placed.x + dx, y: top },
    { x: placed.x + dx, y: bottom },
    { x: placed.x - dx, y: top },
    { x: placed.x - dx, y: bottom },
    { x: left, y: placed.y + dy },
    { x: right, y: placed.y + dy },
    { x: left, y: placed.y - dy },
    { x: right, y: placed.y - dy },
  ];
};

// Whether a block at a place would come nearer a placed one than the gaps
const crowds = (at: Point, block: Block, placed: Box, gap: Point): boolean =>
  Math.abs(at.x - placed.x) < (block.width + placed.block.width) / 2 + gap.x - 0.5 &&
  Math.abs(at.y - placed.y) < (block.height + placed.block.height) / 2 + gap.y - 0.5;

// How far two blocks that share groups are from meeting: side by side at
// the gap kept for a shared group, their facing sides alongside each other
// for a group's room at least. Corner to corner is not meeting: a third
// cluster may stand in the corner, and no outline reaches round it.
const sharedApart = (one: Box, other: Box, gap: Point): number => {
  const across = Math.abs(one.x - other.x) - (one.block.width + other.block.width) / 2;
  const down = Math.abs(one.y - other.y) - (one.block.height + other.block.height) / 2;
  const beside = Math.max(0, across - gap.x - 1) + Math.max(0, down + SLOT.height);
  const above = Math.max(0, down - gap.y - 1) + Math.max(0, across + SLOT.width);
  return Math.min(beside, above);
};

// What a whole drawing costs: the distances of linked clusters weighed by
// their links, how far clusters sharing groups stand apart, the spread from
// the drawing's middle and the bend from its proportions
const drawingCost = (
  boxes: Box[],
  weigh: Weigh,
  shares: (a: number, b: number) => boolean,
  gap: Gap,
  unit: number,
): number => {
  let weighted = 0;
  let total = 0;
  let apart = 0;
  boxes.forEach((one, index) => {
    for (const other of boxes.slice(index + 1)) {
      const a = one.block.cluster.id;
      const b = other.block.cluster.id;
      const weight = weigh(a, b);
      weighted += weight * Math.hypot(one.x - other.x, one.y - other.y);
      total += weight;
      if (shares(a, b)) {
        apart += sharedApart(one, other, gap(a, b));
      }
    }
  });
  const middle = {
    x: boxes.reduce((sum, box) => sum + box.x, 0) / boxes.length,
    y: boxes.reduce((sum, box) => sum + box.y, 0) / boxes.length,
  };
  const spread =
    boxes.reduce((sum, box) => sum + Math.hypot(box.x - middle.x, box.y - middle.y), 0) / boxes.length;
  return (
    (total > 0 ? weighted / total : 0) +
    SHARED_APART_COST * apart +
    PULL_TO_MIDDLE * spread +
    SPREAD_COST * unit * bend(boxes)
  );
};

// A second look over the placed blocks, while two clusters that share
// groups do not meet: each such cluster moved to every free place beside a
// cluster it shares with, and swapped with every other, while the drawing
// costs less, so that clusters placed apart early come to meet
const refineBlocks = (
  boxes: Box[],
  weigh: Weigh,
  shares: (a: number, b: number) => boolean,
  gap: Gap,
  unit: number,
): Box[] => {
  let current = boxes;
  let cost = drawingCost(current, weigh, shares, gap, unit);
  // The blocks of the clusters that share groups with one they do not meet
  const apartOnes = (): number[] =>
    current.flatMap((one, index) =>
      current.some(
        (other) =>
          other !== one &&
          shares(one.block.cluster.id, other.block.cluster.id) &&
          sharedApart(one, other, gap(one.block.cluster.id, other.block.cluster.id)) > 0,
      )
        ? [index]
        : [],
    );
  const free = (moved: Box, others: Box[]) =>
    !others.some((box) =>
      crowds(moved, moved.block, box, gap(moved.block.cluster.id, box.block.cluster.id)),
    );
  const tryTrial = (trial: Box[]): boolean => {
    const trialCost = drawingCost(trial, weigh, shares, gap, unit);
    if (trialCost < cost - 1e-6) {
      current = trial;
      cost = trialCost;
      return true;
    }
    return false;
  };
  for (let pass = 0; pass < REFINE_PASSES; pass += 1) {
    const apart = apartOnes();
    if (apart.length === 0) {
      break;
    }
    let improved = false;
    for (const index of apart) {
      const { block } = current[index];
      const others = current.filter((_unused, at) => at !== index);
      for (const placed of others.filter((other) => shares(block.cluster.id, other.block.cluster.id))) {
        for (const at of besides(placed, block, gap(block.cluster.id, placed.block.cluster.id))) {
          const moved = { block, ...at };
          if (free(moved, others)) {
            const trial = [...current];
            trial[index] = moved;
            improved = tryTrial(trial) || improved;
          }
        }
      }
      for (let other = 0; other < current.length; other += 1) {
        if (other === index) {
          continue;
        }
        const first = { block: current[index].block, x: current[other].x, y: current[other].y };
        const second = { block: current[other].block, x: current[index].x, y: current[index].y };
        const rest = current.filter((_unused, at) => at !== index && at !== other);
        if (free(first, [...rest, second]) && free(second, rest)) {
          const trial = [...current];
          trial[index] = first;
          trial[other] = second;
          improved = tryTrial(trial) || improved;
        }
      }
    }
    if (!improved) {
      break;
    }
  }
  return current;
};

// The blocks placed one by one, in the placing order: each at the free place
// beside one placed whose summed distance to the clusters it is linked to,
// weighed by the links, is the least, with a pull to the middle of the
// placed, a cost for each cluster it shares groups with that it does not
// meet, and a cost for bending the drawing from its proportions; then a
// second look moves and swaps blocks while the whole drawing costs less.
// Linked clusters come to stand side by side, a cluster of no links at the
// edge, and clusters that share groups meet.
const placeBlocks = (blocks: Block[], links: PartnershipLink[]): Box[] => {
  if (blocks.length === 0) {
    return [];
  }
  const weigh = weighing(links);
  const gap = gapping(links);
  const sharing = new Set(
    links.filter((link) => (link.shared ?? 0) > 0).map((link) => linkKey(link.a, link.b)),
  );
  const shares = (a: number, b: number) => sharing.has(linkKey(a, b));
  const unit = blocks.reduce((sum, block) => sum + block.width, 0) / blocks.length;
  const [first, ...rest] = placingOrder(blocks, weigh);
  const boxes: Box[] = [{ block: first, x: 0, y: 0 }];
  for (const block of rest) {
    const total = boxes.reduce((sum, box) => sum + weigh(block.cluster.id, box.block.cluster.id), 0);
    const middle = {
      x: boxes.reduce((sum, box) => sum + box.x, 0) / boxes.length,
      y: boxes.reduce((sum, box) => sum + box.y, 0) / boxes.length,
    };
    const gapTo = (box: Box) => gap(block.cluster.id, box.block.cluster.id);
    let best: Point | null = null;
    let bestCost = Infinity;
    for (const placed of boxes) {
      for (const at of besides(placed, block, gapTo(placed))) {
        if (boxes.some((box) => crowds(at, block, box, gapTo(box)))) {
          continue;
        }
        const pulled =
          total > 0
            ? boxes.reduce(
                (sum, box) =>
                  sum +
                  weigh(block.cluster.id, box.block.cluster.id) *
                    Math.hypot(at.x - box.x, at.y - box.y),
                0,
              ) / total
            : 0;
        const toMiddle = Math.hypot(at.x - middle.x, at.y - middle.y);
        const apart = boxes.reduce(
          (sum, box) =>
            shares(block.cluster.id, box.block.cluster.id)
              ? sum + sharedApart({ block, ...at }, box, gapTo(box))
              : sum,
          0,
        );
        const cost =
          pulled +
          PULL_TO_MIDDLE * toMiddle +
          SHARED_APART_COST * apart +
          SPREAD_COST * unit * bend([...boxes, { block, ...at }]);
        if (cost < bestCost - 1e-6) {
          best = at;
          bestCost = cost;
        }
      }
    }
    boxes.push({ block, ...(best ?? { x: 0, y: 0 }) });
  }
  return refineBlocks(boxes, weigh, shares, gap, unit);
};

// The box an outline holds a group by: its mark and its name beneath, the
// name by the share of its width a name mostly takes
const outlineBox = (entry: Pick<PlacedGroup, "x" | "y" | "size">): OutlineBox => {
  const half = Math.max(entry.size / 2, LABEL_WIDTH * NAME_SHARE);
  return {
    left: entry.x - half,
    top: entry.y - entry.size / 2,
    width: half * 2,
    height: entry.size + LABEL_HEIGHT,
  };
};

// The room a group's box keeps from another's, and from a shape it is not
// in, while a shared group is set
const SHARED_CLEAR = 8;
// How many rings out from the gap a shared group's place is looked for, and
// how many beyond the first place some of its clusters reach, for one more
// of them reach
const SHARED_RINGS = 24;
const SHARED_BETTER_RINGS = 4;

// A cluster whose shape reaches a shared group, and how far either side the
// bulge out to it falls away
interface Reach {
  cluster: number;
  fall: number;
}

// A shared group's place, and the clusters it is in whose shapes reach it
interface SharedPlace {
  at: Point;
  reached: Reach[];
}

// The clusters' shapes while the shared groups are set: each one's shape
// round its own groups, the shared groups set in it so far with the fall of
// each one's bulge, and its shape with the bulges out to them
interface SharedShapes {
  bases: Map<number, RoundShape | null>;
  sharedOf: Map<number, OutlineBox[]>;
  fallsOf: Map<number, number[]>;
  shapeOf: Map<number, RoundShape | null>;
}

// Where a group of several clusters stands: in the middle of the gap
// between each two of their blocks (their facing edges), the mean of those
// middles, then moved out step by step until its box is clear of every
// group placed and outside every shape of a cluster it is not in, where
// the most of its clusters reach it with a bulge that holds no group of
// another cluster. A cluster that cannot reach it draws no bulge to it: the
// group's mark alone tells it belongs there too.
const sharedPlace = (
  clusters: number[],
  boxes: Box[],
  placed: PlacedGroup[],
  size: number,
  shapes: SharedShapes,
): SharedPlace => {
  const boxOf = new Map(boxes.map((box) => [box.block.cluster.id, box]));
  const held = clusters.map((cluster) => boxOf.get(cluster)).filter((box): box is Box => !!box);
  const middles: Point[] = [];
  held.forEach((one, index) => {
    held.slice(index + 1).forEach((other) => {
      const dx = other.x - one.x;
      const dy = other.y - one.y;
      const across = Math.abs(dx) * (one.block.height + other.block.height) >=
        Math.abs(dy) * (one.block.width + other.block.width);
      if (across) {
        const sign = Math.sign(dx) || 1;
        middles.push({
          x: (one.x + (sign * one.block.width) / 2 + other.x - (sign * other.block.width) / 2) / 2,
          y: (one.y + other.y) / 2,
        });
      } else {
        const sign = Math.sign(dy) || 1;
        middles.push({
          x: (one.x + other.x) / 2,
          y: (one.y + (sign * one.block.height) / 2 + other.y - (sign * other.block.height) / 2) / 2,
        });
      }
    });
  });
  const target =
    middles.length > 0
      ? {
          x: middles.reduce((sum, point) => sum + point.x, 0) / middles.length,
          // The middle of its box, not of its mark, in the gap
          y: middles.reduce((sum, point) => sum + point.y, 0) / middles.length - LABEL_HEIGHT / 2,
        }
      : { x: held[0]?.x ?? 0, y: held[0]?.y ?? 0 };
  const clear = (at: Point) => {
    const box = outlineBox({ ...at, size });
    return placed.every((entry) => {
      const other = outlineBox(entry);
      return (
        box.left + box.width + SHARED_CLEAR <= other.left ||
        other.left + other.width + SHARED_CLEAR <= box.left ||
        box.top + box.height + SHARED_CLEAR <= other.top ||
        other.top + other.height + SHARED_CLEAR <= box.top
      );
    });
  };
  // Whether a place stands outside every shape of a cluster it is not in
  const outside = (box: OutlineBox): boolean =>
    Array.from(shapes.shapeOf.entries()).every(
      ([cluster, shape]) =>
        !shape || clusters.includes(cluster) || !touchesRound(shape, box, SHARED_CLEAR),
    );
  // The clusters it is in that reach a place cleanly: the place inside the
  // cluster's shape already, or the bulge out to it holding no group of
  // another cluster, with the softest bulge that does
  const reaching = (box: OutlineBox): Reach[] =>
    clusters.flatMap((cluster) => {
      const base = shapes.bases.get(cluster);
      if (!base) {
        return [{ cluster, fall: BULGE_FALLS[0] }];
      }
      const held = shapes.sharedOf.get(cluster) ?? [];
      const falls = shapes.fallsOf.get(cluster) ?? [];
      const fall = BULGE_FALLS.find((tried) => {
        const bulged = bulgedRound(base, [...held, box], [...falls, tried]);
        return placed.every(
          (entry) =>
            entry.clusters.includes(cluster) ||
            !touchesRound(bulged, outlineBox(entry), SHARED_CLEAR),
        );
      });
      return fall === undefined ? [] : [{ cluster, fall }];
    });
  // How good a place is by the clusters that reach it: the more the better,
  // and of as many, one its own cluster reaches
  const worth = (reached: Reach[]): number =>
    reached.length * 2 + (reached.some((reach) => reach.cluster === clusters[0]) ? 1 : 0);
  // Out from the target on growing rings: the nearest clear place outside
  // the other clusters' shapes that every cluster it is in reaches; else,
  // looking a few rings further than the first such place, the one worth
  // most, the nearer first
  let best: SharedPlace | null = null;
  let bestRing = SHARED_RINGS;
  for (let ring = 0; ring <= Math.min(SHARED_RINGS, bestRing + SHARED_BETTER_RINGS); ring += 1) {
    const steps = ring === 0 ? 1 : 8 * ring;
    for (let step = 0; step < steps; step += 1) {
      const angle = (step / steps) * TURN;
      const at = {
        x: target.x + Math.cos(angle) * ring * (SLOT.width / 3),
        y: target.y + Math.sin(angle) * ring * (SLOT.height / 3),
      };
      const box = outlineBox({ ...at, size });
      if (!clear(at) || !outside(box)) {
        continue;
      }
      const reached = reaching(box);
      if (reached.length === clusters.length) {
        return { at, reached };
      }
      if (reached.length > 0 && (!best || worth(reached) > worth(best.reached))) {
        best = { at, reached };
        bestRing = Math.min(bestRing, ring);
      }
    }
  }
  return best ?? { at: target, reached: [] };
};

// How far a drawing's proportions are from those aimed at, on a log scale
const bend = (boxes: Box[]): number => {
  const lefts = boxes.map((box) => box.x - box.block.width / 2);
  const rights = boxes.map((box) => box.x + box.block.width / 2);
  const tops = boxes.map((box) => box.y - box.block.height / 2);
  const bottoms = boxes.map((box) => box.y + box.block.height / 2);
  const width = Math.max(...rights) - Math.min(...lefts);
  const height = Math.max(1, Math.max(...bottoms) - Math.min(...tops));
  return Math.abs(Math.log(width / height / SPREAD_ASPECT));
};

// The group in focus: the response's focus, else its first group
export const focusOf = (data: Partnerships): PartnershipNode | null =>
  data.nodes.find((node) => node.id === data.focus?.id) ?? data.nodes[0] ?? null;

// The strength of each group's tie to the group in focus
export const tiesToFocus = (data: Partnerships, focusId: string): Map<string, PartnershipPair> => {
  const ties = new Map<string, PartnershipPair>();
  for (const pair of data.pairs) {
    if (pair.a === focusId && pair.b !== focusId) {
      ties.set(pair.b, pair);
    } else if (pair.b === focusId && pair.a !== focusId) {
      ties.set(pair.a, pair);
    }
  }
  return ties;
};

// The partners of the focus, the strongest tie first; of two tied as
// strongly, in the response's order
export const rankedPartners = (
  data: Partnerships,
): { node: PartnershipNode; pair: PartnershipPair | null }[] => {
  const focus = focusOf(data);
  if (!focus) {
    return [];
  }
  const ties = tiesToFocus(data, focus.id);
  return data.nodes
    .filter((node) => node.id !== focus.id)
    .map((node, index) => ({ node, pair: ties.get(node.id) ?? null, index }))
    .sort((a, b) => (b.pair?.strength ?? 0) - (a.pair?.strength ?? 0) || a.index - b.index)
    .map(({ node, pair }) => ({ node, pair }));
};

// The partners on each ring, the inner first: each ring takes its share,
// and past it the partners tied as strongly as its last while it has room
// to stretch
export const ringsOf = <T>(ranked: T[], strengthOf: (entry: T) => number): T[][] => {
  const rings: T[][] = [];
  let current: T[] = [];
  for (const entry of ranked) {
    const share = RING_CAPACITY * (rings.length + 1);
    const last = current[current.length - 1];
    const same = last !== undefined && strengthOf(last) === strengthOf(entry);
    if (current.length >= share && !(same && current.length < share * RING_STRETCH)) {
      rings.push(current);
      current = [];
    }
    current.push(entry);
  }
  if (current.length > 0) {
    rings.push(current);
  }
  return rings;
};

// The point of a ring at an angle from its top, clockwise
const pointAt = (ry: number, angle: number): Point => ({
  x: ry * RING_ASPECT * Math.cos(TOP + angle),
  y: ry * Math.sin(TOP + angle),
});

// The angles of `count` partners spaced evenly round a ring, the first at
// the top; every other ring is turned by half a space, so that the spokes
// of two rings do not fall on one another
const ringAngles = (count: number, ring: number): number[] => {
  const step = TURN / Math.max(1, count);
  const turn = ring % 2 === 0 ? step / 2 : 0;
  return Array.from({ length: count }, (_unused, index) => turn + index * step);
};

// Whether two partners are too near for their names
const clash = (a: Point, b: Point): boolean =>
  Math.abs(a.x - b.x) < SLOT.width && Math.abs(a.y - b.y) < SLOT.height;

// The smallest ring, no lower than `floor`, on which no two neighbours clash
const fittedRing = (floor: number, angles: number[]): number => {
  let ry = floor;
  const clashes = () =>
    angles.length > 1 &&
    angles.some((angle, index) =>
      clash(pointAt(ry, angle), pointAt(ry, angles[(index + 1) % angles.length])),
    );
  for (let step = 0; step < 2000 && clashes(); step += 1) {
    ry += RING_GROW;
  }
  return ry;
};

// The partners of one organization: it at the centre, its partners on
// rings by the strength of their tie to it, the strongest nearest
export const layoutPartners = (data: Partnerships): PartnershipsLayout => {
  const focus = focusOf(data);
  if (!focus) {
    return { nodes: [], shapes: [], ties: [], rings: [], loose: null };
  }
  const ranked = rankedPartners(data);
  const strongest = Math.max(0, ...ranked.map((entry) => entry.pair?.strength ?? 0));
  const rings = ringsOf(ranked, (entry) => entry.pair?.strength ?? 0);
  const placed: PlacedGroup[] = [
    {
      node: focus,
      x: 0,
      y: 0,
      size: FOCUS_MARK,
      ring: 0,
      tie: null,
      clusters: membershipsOf(data, focus),
    },
  ];
  const guides: PlacedRing[] = [];
  let previous = 0;
  rings.forEach((held, index) => {
    const ring = index + 1;
    const angles = ringAngles(held.length, ring);
    const ry = fittedRing(previous === 0 ? MIN_RING_ONE : previous + MIN_RING_STEP, angles);
    guides.push({ ring, rx: round(ry * RING_ASPECT), ry: round(ry) });
    held.forEach((entry, place) => {
      const point = pointAt(ry, angles[place]);
      const tie = entry.pair?.strength ?? 0;
      placed.push({
        node: entry.node,
        x: round(point.x),
        y: round(point.y),
        size: markSize(tie, strongest),
        ring,
        tie,
        clusters: membershipsOf(data, entry.node),
      });
    });
    previous = ry;
  });
  const order = new Map(data.nodes.map((node, index) => [node.id, index]));
  placed.sort((a, b) => (order.get(a.node.id) ?? 0) - (order.get(b.node.id) ?? 0));
  const at = new Map(placed.map((entry) => [entry.node.id, entry]));
  return {
    nodes: placed,
    shapes: [],
    ties: placeTies(data, at, focus.id),
    rings: guides,
    loose: null,
  };
};

// What a group in focus lights: its own ties and the groups at their far
// ends
export interface Lit {
  // Ties drawn in the accent
  direct: Set<string>;
  // Ties drawn in a fainter accent: a cluster's ties out of it
  route: Set<string>;
  // Groups kept bright
  nodes: Set<string>;
}

export const litByGroup = (layout: PartnershipsLayout, id: string): Lit => {
  const lit: Lit = { direct: new Set(), route: new Set(), nodes: new Set([id]) };
  for (const tie of layout.ties) {
    if (tie.pair.a === id || tie.pair.b === id) {
      lit.direct.add(tie.id);
      lit.nodes.add(tie.pair.a === id ? tie.pair.b : tie.pair.a);
    }
  }
  return lit;
};

// What a cluster lights: its ties inside it, its ties out of it fainter,
// and its own groups
export const litByCluster = (layout: PartnershipsLayout, cluster: number): Lit => {
  // Its groups, those it shares with other clusters too
  const inside = new Set(
    layout.nodes.filter((entry) => entry.clusters.includes(cluster)).map((entry) => entry.node.id),
  );
  const lit: Lit = { direct: new Set(), route: new Set(), nodes: new Set(inside) };
  for (const tie of layout.ties) {
    const a = inside.has(tie.pair.a);
    const b = inside.has(tie.pair.b);
    if (a && b) {
      lit.direct.add(tie.id);
    } else if (a || b) {
      lit.route.add(tie.id);
    }
  }
  return lit;
};

// What a pair lights: its tie and its two groups
export const litByPair = (pair: Pick<PartnershipPair, "a" | "b">): Lit => ({
  direct: new Set([tieId(pair)]),
  route: new Set(),
  nodes: new Set([pair.a, pair.b]),
});
